import { useState, type FormEvent, type JSX } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth.js';
import { Icon, Logo } from '../components/Icon.js';
import { BRAND } from '../branding.js';

// Коды ошибок, с которыми сервер возвращает на /login после входа через Google
// (routes/admin/auth.ts). На установке «только Google» формы пароля нет —
// и подсказка «войдите по паролю» вела бы в никуда.
const GOOGLE_ERRORS: Record<string, string> = {
  google_no_access:
    'Этот Google-аккаунт не привязан к пользователю админки. Войдите аккаунтом с тем email, который заведён в «Пользователях», или попросите администратора добавить ваш.',
  google_cancelled: 'Вход через Google отменён.',
  google_failed: BRAND.passwordLogin
    ? 'Не удалось войти через Google. Попробуйте ещё раз или войдите по паролю.'
    : 'Не удалось войти через Google. Попробуйте ещё раз; если не выходит — напишите администратору.',
  google_disabled: 'Вход через Google здесь не настроен. Войдите по паролю.',
};

function GoogleLogo(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.9 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}

export function Login(): JSX.Element {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(() => {
    const code = params.get('error');
    return code ? (GOOGLE_ERRORS[code] ?? GOOGLE_ERRORS.google_failed!) : null;
  });
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await login(email, password);
      nav('/admin', { replace: true });
    } catch {
      setErr('Неверный email или пароль');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        background: 'var(--bg)',
        padding: 20,
      }}
    >
      <div style={{ width: 380, maxWidth: '100%' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 24,
            justifyContent: 'center',
          }}
        >
          <Logo size={28} />
          <div style={{ fontWeight: 600, fontSize: 17, letterSpacing: -0.3 }}>{BRAND.name}</div>
        </div>
        <div className="card" style={{ padding: 28 }}>
          <h2 style={{ margin: '0 0 4px', fontSize: 19, fontWeight: 600, letterSpacing: -0.3 }}>
            Вход
          </h2>
          <p style={{ margin: '0 0 20px', color: 'var(--fg-subtle)', fontSize: 13 }}>
            Управление платёжными продуктами
          </p>
          {BRAND.googleLogin && (
            <>
              {/* Обычный переход страницы, а не роутер SPA: это адрес API,
                  который уведёт на Google и обратно. */}
              <a
                href="/admin/api/auth/google/start"
                className="btn"
                style={{ justifyContent: 'center', gap: 10, width: '100%', textDecoration: 'none' }}
              >
                <GoogleLogo />
                Войти через Google
              </a>
              {BRAND.passwordLogin && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    margin: '18px 0',
                    color: 'var(--fg-subtle)',
                    fontSize: 12,
                  }}
                >
                  <div style={{ flex: 1, height: 1, background: 'var(--hairline)' }} />
                  или по паролю
                  <div style={{ flex: 1, height: 1, background: 'var(--hairline)' }} />
                </div>
              )}
              {!BRAND.passwordLogin && err && (
                <div style={{ fontSize: 13, color: 'var(--danger-fg)', marginTop: 14 }}>{err}</div>
              )}
            </>
          )}
          {/* Установка «только Google» (ADMIN_PASSWORD_LOGIN=off): формы пароля нет. */}
          {BRAND.passwordLogin && (
          <form onSubmit={onSubmit} className="stack">
            <div className="field">
              <label className="field-label">Email</label>
              <div className="field-prefix">
                <span className="prefix-txt" style={{ paddingLeft: 10, paddingRight: 8 }}>
                  <Icon name="mail" size={13} />
                </span>
                <input
                  className="input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus={!BRAND.googleLogin}
                />
              </div>
            </div>
            <div className="field">
              <label className="field-label">Пароль</label>
              <div className="field-prefix">
                <span className="prefix-txt" style={{ paddingLeft: 10, paddingRight: 8 }}>
                  <Icon name="lock" size={13} />
                </span>
                <input
                  className="input"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••"
                />
              </div>
            </div>
            {err && (
              <div style={{ fontSize: 13, color: 'var(--danger-fg)' }}>{err}</div>
            )}
            <button
              className="btn btn-primary"
              type="submit"
              disabled={busy}
              style={{ justifyContent: 'center', marginTop: 6 }}
            >
              {busy ? 'Вход…' : 'Войти'}
            </button>
            <div style={{ textAlign: 'center', marginTop: 4 }}>
              <Link
                to="/forgot-password"
                style={{
                  fontSize: 13,
                  color: 'var(--fg-subtle)',
                  textDecoration: 'none',
                }}
              >
                Забыли пароль?
              </Link>
            </div>
          </form>
          )}
        </div>
      </div>
    </div>
  );
}
